import { CheckCircle, CopyDuplicateObject, Trash } from '@griply/assets';
import { useTaskCalendarSyncControls } from '@griply/hooks';
import { useCalendarTaskSync } from 'features/external-connections';
import {
  useDeleteTask,
  useDuplicateTasks,
  useTaskTodayControls,
} from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarSyncMenuItems } from 'shared/components/ui/calendar-sync-menu';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { InteractionType } from 'shared/types/data-type';
import { Task } from 'shared/types/task';

export type ActionTaskContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'interactionType' | 'onOutside'
> & {
  task: Task;
  onClose: () => void;
  onAction?: () => void;
};

export const ActionTaskContextMenu: React.FC<ActionTaskContextMenuProps> = ({
  task,
  onClose,
  onAction,
  ...rest
}) => {
  const { t } = useTranslation();

  const { onComplete } = useTaskTodayControls();
  const { submit: deleteTask } = useDeleteTask();
  const duplicateTask = useDuplicateTasks();

  const tasks = useMemo(() => [task], [task]);
  const calendarSync = useCalendarTaskSync(tasks);
  const { syncState, toggleSync, toggleVisibility } =
    useTaskCalendarSyncControls(task, calendarSync);

  const _toggleSync = () => {
    toggleSync();
    onAction?.();
    onClose();
  };
  const _toggleVisibility = () => {
    toggleVisibility();
    onAction?.();
    onClose();
  };

  const _completeTask = () => {
    onComplete(task, undefined, { keepDetailOpen: true });
    onAction?.();
    onClose();
  };
  const _duplicateTask = () => {
    duplicateTask([task]);
    onAction?.();
    onClose();
  };
  const _deleteTask = () => {
    deleteTask(task);
    onAction?.();
    onClose();
  };

  return (
    <PopupMenu
      {...rest}
      interactionType={InteractionType.Task}
      onOutside={onClose}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={CheckCircle} strokeWidth={1.5} />}
            onClick={_completeTask}
          >
            {t(
              task.completedAt
                ? 'context-menus.calendar-events.labels.goal.un-complete.label'
                : 'context-menus.calendar-events.labels.goal.complete.label',
            )}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={CopyDuplicateObject} strokeWidth={1.5} />}
            onClick={_duplicateTask}
          >
            {t('context-menus.calendar-events.labels.goal.duplicate.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
      {!!syncState && (
        <PopupMenuList hasBottomBorder>
          <CalendarSyncMenuItems
            isSynced={syncState.isSynced}
            isHidden={syncState.isHidden}
            onToggleSync={_toggleSync}
            onToggleVisibility={_toggleVisibility}
          />
        </PopupMenuList>
      )}
      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            start={<Icon icon={Trash} strokeWidth={1.5} />}
            onClick={_deleteTask}
            startColor={Colors.Error}
            textColor={Colors.Error}
          >
            {t('context-menus.calendar-events.labels.goal.delete.label')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
